import { decodeControllerSwitchType } from "building-generation/elements/electrical/generatedElectricalUtils";
import { getWallPath } from "building-generation/elements/walls/wall";
import type { Building } from "building-types/building/buildingTypes";
import type { Casework } from "building-types/casework/caseworkTypes";
import type { GeneratedCeiling } from "building-types/ceilings/ceiling";
import type { ElectricalKeyAttributes } from "building-types/dataSequences/builtin/ElectricalKAM";
import type {
  BuildingEntityFilter,
  FilterableBuildingEntity,
  TrimEntity,
} from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { EntityKeyAttributes } from "building-types/dataSequences/measures";
import type { ColumnHeaderOptions } from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { DATA_TABLE_SYMBOL_COLUMN_ID, type DataTableColumnConfig } from "building-types/dataTables/dataTable";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { GeneratedElectricalGroup } from "building-types/electrical/generatedElectricalTypes";
import type { StandardFoundationKeyMeasure } from "building-types/foundations/standardFoundationKeyMeasureTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Prop } from "building-types/props/propTypes";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { EvaluatedDataTable } from "building-types/views/planSets";
import type { Framing } from "building-types/walls/wallFramingTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { isControllerSwitchType, isControllerType } from "doc/elements/electrical/electricalTools";
import { docEntityHandleIsLabelable, isDocEntityHandle } from "doc/entity/docEntityHandleTools";
import { BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_INCLUDES_SYMBOL_COLUMN } from "doc/utils/dataSequenceTools";
import { docEntityContainsLabel } from "doc/utils/labelTools";
import { generateController } from "lib/house/annotation/electrical/Controller";
import { generateLoad } from "lib/house/annotation/electrical/Load";
import { emptySVG } from "lib/house/annotation/EmptySVG";
import type { DataTableCellWithIndex } from "./dataTableSelectors";

function symbolColumnPredicate(
  config: DataTableColumnConfig | undefined,
  sequenceType: SystemDataSequenceEntityType,
): boolean {
  return !!(!config?.isHidden && BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_INCLUDES_SYMBOL_COLUMN[sequenceType]);
}

export function makeDataTableKAMRowCellGenerator(
  sequenceType: SystemDataSequenceEntityType,
  building: Building,
  columnConfig: Table<DataTableColumnConfig>,
): ((attributes: EntityKeyAttributes, maxIndex: number) => DataTableCellWithIndex[]) | undefined {
  const config = columnConfig[DATA_TABLE_SYMBOL_COLUMN_ID];

  if (!symbolColumnPredicate(config, sequenceType)) {
    return undefined;
  }

  return (attributes: EntityKeyAttributes, maxIndex: number) => {
    const electricalSymbol = (attributes as ElectricalKeyAttributes).entityName?.value;

    const cellIndex = config?.index ?? maxIndex + 1;

    const makeSymbolCell = (value: string = emptySVG()) => {
      return [{ index: cellIndex, cell: { type: "SYMBOL", value } } as const];
    };

    if (!electricalSymbol) {
      return makeSymbolCell();
    }

    // is the type a load descriptor table key?
    const loadDescriptor = building.settings.electricalItemCatalog.loadTypes[electricalSymbol];
    if (loadDescriptor) {
      return makeSymbolCell(generateLoad({ loadDescriptor, switchedOutletFillColor: "black" }));
    }

    // is the type a controller?
    const [controllerType, switchType] = decodeControllerSwitchType(electricalSymbol);
    if (isControllerType(controllerType)) {
      return makeSymbolCell(
        generateController({
          switchType: switchType && isControllerSwitchType(switchType) ? switchType : undefined,
          dimmerMarkerAsText: true,
        }),
      );
    }

    return makeSymbolCell();
  };
}

export function makeDataTableKAMHeaderCellGenerator(
  sequenceType: SystemDataSequenceEntityType,
  columnConfig: Table<DataTableColumnConfig>,
): ((headers: Table<ColumnHeaderOptions>) => Table<ColumnHeaderOptions>) | undefined {
  const config = columnConfig[DATA_TABLE_SYMBOL_COLUMN_ID];

  if (!symbolColumnPredicate(config, sequenceType)) {
    return undefined;
  }

  const configWithDisplayName = {
    ...config,
    displayName: config?.displayName ?? DATA_TABLE_SYMBOL_COLUMN_ID,
  };

  return (headers: Table<ColumnHeaderOptions>) => {
    const newHeaders = {
      ...headers,
      [DATA_TABLE_SYMBOL_COLUMN_ID]: configWithDisplayName,
    };
    return newHeaders;
  };
}

export function makeDataSequenceKAMFilter(
  sequenceType: SystemDataSequenceEntityType,
  dataTable: EvaluatedDataTable,
  building: Building,
): BuildingEntityFilter | undefined {
  const levelFilter = makeDataSequenceKAMLevelFilter(sequenceType, dataTable);
  const labelFilter = makeDataSequenceKAMLabelFilter(sequenceType, dataTable, building);

  if (!levelFilter && !labelFilter) {
    return undefined;
  }

  return (entity: FilterableBuildingEntity) => {
    if (levelFilter && !levelFilter(entity)) {
      return false;
    }
    if (labelFilter && !labelFilter(entity)) {
      return false;
    }
    return true;
  };
}

function makeDataSequenceKAMLevelFilter(
  sequenceType: SystemDataSequenceEntityType,
  dataTable: EvaluatedDataTable,
): BuildingEntityFilter | undefined {
  if (!dataTable.levelId) {
    return undefined;
  }

  switch (sequenceType) {
    case "ELECTRICAL":
      return (entity: FilterableBuildingEntity) => {
        if ("levelId" in entity && entity.levelId === dataTable.levelId) {
          return true;
        }
        return false;
      };
    default:
      return undefined;
  }
}

function makeDataSequenceKAMLabelFilter(
  sequenceType: SystemDataSequenceEntityType,
  dataTable: EvaluatedDataTable,
  building: Building,
): BuildingEntityFilter | undefined {
  const reportingUnitLabelId = dataTable.selectedReportingUnitLabelId;
  if (!reportingUnitLabelId) {
    return undefined;
  }

  const hasLabel = (handle?: DocEntityHandle) => {
    return !!(
      handle &&
      docEntityHandleIsLabelable(handle) &&
      docEntityContainsLabel(building.doc, handle, reportingUnitLabelId)
    );
  };

  const roomHasLabel = (room: Room | null | undefined) => {
    return !!(room && hasLabel(roomEntityHandleForSelectingReportingUnit(room)));
  };

  switch (sequenceType) {
    case "CEILING":
      return (entity: FilterableBuildingEntity) => {
        if ("section" in entity && "roomId" in entity.section) {
          const ceiling = entity as GeneratedCeiling;
          return roomHasLabel(building.rooms[ceiling.section.roomId]);
        }
        return true;
      };
    case "BUILDING_AREA":
    case "FLOORING":
    case "INTERIOR_FINISH":
      return (entity: FilterableBuildingEntity) => {
        if ("roomHandle" in entity) {
          return roomHasLabel(entity as Room);
        }
        return true;
      };
    case "WALL":
      return (entity: FilterableBuildingEntity) => {
        if ("entityType" in entity && entity.entityType === "WALL") {
          const wall = entity as Wall;
          const path = getWallPath(wall);
          return !!(roomHasLabel(path.leftRoom) || roomHasLabel(path.rightRoom));
        }
        return true;
      };
    case "OPENING_TRIM":
    case "MILLWORK":
      return (entity: FilterableBuildingEntity) => {
        if ("handles" in entity) {
          const trim = entity as TrimEntity;
          if (trim.handles.some((h) => hasLabel(h))) {
            return true;
          }
          if (trim.room) {
            return roomHasLabel(trim.room);
          }
          return false;
        } else if (isDocEntityHandle(entity)) {
          return hasLabel(entity);
        }
        return true;
      };
    case "FRAMING":
      return (entity: FilterableBuildingEntity) => {
        if ("roomHandle" in entity) {
          return roomHasLabel(entity as Room);
        }
        if ("meta" in entity) {
          const framing = entity as Framing;
          const adjacentRooms = framing.meta?.adjacentRooms;
          if (!adjacentRooms) {
            return true;
          }
          const [front, back] = adjacentRooms;
          return (
            roomHasLabel(front ? building.rooms[front.id] : undefined) ||
            roomHasLabel(back ? building.rooms[back.id] : undefined)
          );
        }
        return true;
      };
    case "ROOF":
    case "ROOF_AREA":
    case "ROOF_ANALYTIC_AREA":
    case "ROOF_ANALYTIC_LENGTH":
    case "ROOF_RIDGE":
    case "ROOF_RIDGE_VENT":
    case "ROOF_VALLEY":
    case "ROOF_EAVE":
    case "ROOF_FLASHING":
      return (entity: FilterableBuildingEntity) => {
        if ("entityType" in entity && entity.entityType === "ROOF_SYSTEM") {
          const system = entity as GeneratedRoofSystem;
          return Object.values(system.roofs).some((roof) => hasLabel(roof.docEntityHandle));
        }
        if ("docEntityHandle" in entity) {
          const roof = entity as GeneratedRoof;
          return hasLabel(roof.docEntityHandle) ?? false;
        }
        return true;
      };
    case "DOOR":
    case "WINDOW":
      return (entity: FilterableBuildingEntity) => {
        const placement = entity as GeneratedDoorPlacement | GeneratedWindowPlacement;
        return roomHasLabel(placement.frontRoom) || roomHasLabel(placement.backRoom);
      };
    case "PROP":
      return (entity: FilterableBuildingEntity) => {
        if ("room" in entity) {
          return roomHasLabel((entity as Prop).room);
        }
        return true;
      };
    case "CASEWORK":
      return (entity: FilterableBuildingEntity) => {
        if ("room" in entity) {
          return roomHasLabel((entity as Casework).room);
        }
        return true;
      };
    case "FOOTING":
    case "SLAB":
      return (entity: FilterableBuildingEntity) => {
        if ("handles" in entity) {
          const measure = entity as StandardFoundationKeyMeasure;
          return measure.handles.some((h) => hasLabel(h));
        }
        return true;
      };
    case "ELECTRICAL":
      return (entity: FilterableBuildingEntity) => {
        if ("loads" in entity && "controllers" in entity) {
          const group = entity as GeneratedElectricalGroup;
          for (const load of group.loads) {
            if (roomHasLabel(building.rooms[load.sourceRoomHandle.id])) {
              return true;
            }
          }
          for (const controller of group.controllers) {
            if (roomHasLabel(building.rooms[controller.sourceRoomHandle.id])) {
              return true;
            }
          }
          return false;
        }
        return true;
      };
    default:
      return undefined;
  }
}
